/**
 * /admin/analytics — daily visits chart + headline stat cards.
 *
 * Intentionally minimal per user spec:
 *   - Last 30 days of visits as a line chart
 *   - 3 stat cards: today, total (window), unique visitors (window)
 *   - Period selector (7 / 30 / 90 days)
 *
 * Backed entirely by first-party data stored in MongoDB by the
 * /api/analytics/visit endpoint. No PostHog, no GA, no third-party
 * cookies, no SDK to update.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import {
    LogOut,
    LineChart as LineChartIcon,
    Loader2,
    Users,
    Eye,
    Calendar,
} from "lucide-react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Area,
    AreaChart,
} from "recharts";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
    { value: 7, label: "7 days" },
    { value: 30, label: "30 days" },
    { value: 90, label: "90 days" },
];

function StatCard({ label, value, sub, icon: Icon }) {
    return (
        <div className="rounded-2xl border border-[rgba(212,175,55,0.18)] bg-[#0b0e13] p-5">
            <div className="flex items-start justify-between">
                <p className="text-[10px] uppercase tracking-[0.3em] text-brand-muted">
                    {label}
                </p>
                {Icon ? <Icon className="h-4 w-4 text-brand-gold2" /> : null}
            </div>
            <p
                className="mt-3 font-serif text-4xl text-brand-ivory"
                data-testid={`analytics-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
                {value}
            </p>
            {sub ? (
                <p className="mt-1 text-xs text-brand-muted">{sub}</p>
            ) : null}
        </div>
    );
}

function formatDate(iso) {
    // "2026-02-25" → "Feb 25" — short labels keep the X-axis legible.
    try {
        const d = new Date(`${iso}T00:00:00Z`);
        return d.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            timeZone: "UTC",
        });
    } catch {
        return iso;
    }
}

function ChartTooltip({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload;
    return (
        <div className="rounded-lg border border-[rgba(212,175,55,0.35)] bg-[#0b0e13] px-3 py-2 text-xs text-brand-ivory shadow-lg">
            <p className="font-medium">{formatDate(label)}</p>
            <p className="mt-1 text-brand-gold2">
                {row.visits.toLocaleString()} visits
            </p>
            <p className="text-brand-muted">
                {row.unique_visitors.toLocaleString()} unique
            </p>
        </div>
    );
}

export default function AdminAnalyticsPage() {
    const { token, admin, logout, loading: authLoading } = useAuth();
    const [days, setDays] = useState(30);
    const [total, setTotal] = useState(null);
    const [series, setSeries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        Promise.all([
            axios.get(`${API}/admin/analytics/total?days=${days}`),
            axios.get(`${API}/admin/analytics/daily?days=${days}`),
        ])
            .then(([t, d]) => {
                if (cancelled) return;
                setTotal(t.data);
                setSeries(d.data.series || []);
            })
            .catch((e) => {
                if (!cancelled)
                    setError(
                        e?.response?.data?.detail ||
                            "Unable to load analytics data."
                    );
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [days, token]);

    // Memoised so recharts doesn't re-mount the SVG on every render.
    const chartData = useMemo(() => series, [series]);

    if (authLoading)
        return (
            <main className="flex min-h-screen items-center justify-center bg-brand-bg text-brand-ivory">
                <Loader2 className="h-5 w-5 animate-spin" />
            </main>
        );
    if (!token) return <Navigate to="/admin/login" replace />;

    return (
        <main
            data-testid="admin-analytics-page"
            className="min-h-screen bg-brand-bg text-brand-ivory"
        >
            {/* Top nav — mirrors the layout used by the other admin pages
                so the user stays oriented. The current page's label is
                rendered as a span (not a Link). */}
            <header className="border-b border-[rgba(212,175,55,0.18)] px-6 py-4">
                <div className="mx-auto flex max-w-6xl items-center justify-between">
                    <div className="flex flex-wrap items-center gap-3">
                        <Link
                            to="/admin/videos"
                            data-testid="admin-link-videos"
                            className="text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft hover:text-brand-gold"
                        >
                            Videos
                        </Link>
                        <span className="text-brand-muted">·</span>
                        <Link
                            to="/admin/blog"
                            data-testid="admin-link-blog"
                            className="text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft hover:text-brand-gold"
                        >
                            Blog
                        </Link>
                        <span className="text-brand-muted">·</span>
                        <Link
                            to="/admin/portal-users"
                            className="text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft hover:text-brand-gold"
                        >
                            Portal users
                        </Link>
                        <span className="text-brand-muted">·</span>
                        <span
                            className="text-[11px] uppercase tracking-[0.3em] text-brand-ivory"
                            data-testid="admin-analytics-active"
                        >
                            Analytics
                        </span>
                        <span className="text-brand-muted">·</span>
                        <Link
                            to="/admin/integrations"
                            className="text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft hover:text-brand-gold"
                        >
                            Integrations
                        </Link>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                        <span className="text-brand-muted">{admin?.email}</span>
                        <button
                            type="button"
                            onClick={logout}
                            data-testid="admin-logout"
                            className="inline-flex items-center gap-1 rounded-full border border-white/20 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-brand-muted hover:bg-white/10"
                        >
                            <LogOut className="h-3 w-3" />
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            <section className="mx-auto max-w-6xl px-6 py-10">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft">
                            Site analytics · first-party
                        </p>
                        <h1 className="mt-2 font-serif text-4xl text-brand-ivory">
                            Daily visits
                        </h1>
                        <p className="mt-2 max-w-2xl text-sm text-brand-muted">
                            Server-side page-view counts. Admin clicks and
                            customer-portal traffic are excluded. Bot UAs
                            (Googlebot, monitoring tools, scrapers) are
                            filtered out by default.
                        </p>
                    </div>
                    <div
                        className="inline-flex rounded-full border border-[rgba(212,175,55,0.25)] p-1"
                        data-testid="analytics-period-selector"
                    >
                        {PERIODS.map((p) => (
                            <button
                                key={p.value}
                                type="button"
                                data-testid={`analytics-period-${p.value}`}
                                onClick={() => setDays(p.value)}
                                className={
                                    "rounded-full px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] transition-colors " +
                                    (days === p.value
                                        ? "bg-brand-gold text-[#0b0e13]"
                                        : "text-brand-muted hover:text-brand-ivory")
                                }
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>

                {error ? (
                    <div className="mt-8 rounded-lg border border-rose-500/40 bg-rose-500/5 p-4 text-sm text-rose-100">
                        {error}
                    </div>
                ) : null}

                {/* Stat cards */}
                <div className="mt-8 grid gap-4 sm:grid-cols-3">
                    <StatCard
                        label="Today"
                        icon={Calendar}
                        value={
                            loading ? (
                                <Loader2 className="h-6 w-6 animate-spin" />
                            ) : (
                                (total?.today_visits ?? 0).toLocaleString()
                            )
                        }
                        sub="visits since 00:00 UTC"
                    />
                    <StatCard
                        label="Total visits"
                        icon={Eye}
                        value={
                            loading ? (
                                <Loader2 className="h-6 w-6 animate-spin" />
                            ) : (
                                (total?.total_visits ?? 0).toLocaleString()
                            )
                        }
                        sub={`over the last ${days} days`}
                    />
                    <StatCard
                        label="Unique visitors"
                        icon={Users}
                        value={
                            loading ? (
                                <Loader2 className="h-6 w-6 animate-spin" />
                            ) : (
                                (total?.unique_visitors ?? 0).toLocaleString()
                            )
                        }
                        sub={`over the last ${days} days`}
                    />
                </div>

                {/* Chart */}
                <div
                    className="mt-8 rounded-2xl border border-[rgba(212,175,55,0.18)] bg-[#0b0e13] p-6"
                    data-testid="analytics-chart-container"
                >
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-brand-muted">
                        <LineChartIcon className="h-3.5 w-3.5" />
                        Visits per day · last {days} days
                    </div>
                    <div
                        className="mt-4"
                        style={{ width: "100%", height: 320, minHeight: 320 }}
                    >
                        {loading ? (
                            <div className="flex h-full items-center justify-center text-brand-muted">
                                <Loader2 className="h-5 w-5 animate-spin" />
                            </div>
                        ) : chartData.length === 0 ? (
                            <p className="py-12 text-center text-sm text-brand-muted">
                                No visits recorded yet — once the site
                                receives traffic, the chart will populate
                                automatically.
                            </p>
                        ) : (
                            <ResponsiveContainer
                                width="100%"
                                height="100%"
                                debounce={50}
                            >
                                <AreaChart
                                    data={chartData}
                                    margin={{
                                        top: 10,
                                        right: 12,
                                        left: 0,
                                        bottom: 0,
                                    }}
                                >
                                    <defs>
                                        <linearGradient
                                            id="goldFill"
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1"
                                        >
                                            <stop
                                                offset="0%"
                                                stopColor="#d4af37"
                                                stopOpacity={0.35}
                                            />
                                            <stop
                                                offset="100%"
                                                stopColor="#d4af37"
                                                stopOpacity={0}
                                            />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="rgba(255,255,255,0.06)"
                                    />
                                    <XAxis
                                        dataKey="date"
                                        tickFormatter={formatDate}
                                        stroke="#7a7a7a"
                                        tick={{ fontSize: 11 }}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis
                                        stroke="#7a7a7a"
                                        tick={{ fontSize: 11 }}
                                        allowDecimals={false}
                                    />
                                    <Tooltip content={<ChartTooltip />} />
                                    <Area
                                        type="monotone"
                                        dataKey="visits"
                                        stroke="#d4af37"
                                        strokeWidth={2}
                                        fill="url(#goldFill)"
                                        isAnimationActive
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                <p className="mt-6 text-xs text-brand-muted">
                    Data range: {total?.start_date} → {total?.end_date} (UTC).
                    Counts reset at 00:00 UTC. To include bot traffic, add{" "}
                    <code>?include_bots=true</code> to the API URL.
                </p>
            </section>
        </main>
    );
}
